import {
  CHECKOUT_RULE_DOCUMENT_TYPES,
  CheckoutRuleDocumentType,
} from '../interfaces'
import type { SessionCheckoutRule } from '../graphQL/queries/getSession/types'
import type {
  ICheckoutRuleAttributes,
  ICheckoutRuleValue,
} from '../graphQL/queries/getCheckoutRule/types'
import { selectCheckoutRuleSettingByType } from '../redux/slices/checkout/checkout'
import type { AutoSettleStatus } from '../redux/slices/checkout/types'
import type { RootState } from '../redux/store'
import { ESCAPES_ORIGIN, resolveCheckoutRuleRoute } from './checkoutRuleRoute'

/**
 * Has the shopper chosen a value for this checkout rule?
 *
 * One module, because two subsystems need the same answer and must not drift:
 * `utils/cartCheckoutPanel.ts` renders the pick on the cart's Entrega/Pago
 * rows, and COM-1046's summary CTA decides between *Continuar* and *Realizar
 * pedido* on it. COM-1048 reads it again after auto-settling a one-option rule.
 *
 * **Opening a picker is not choosing** (Product, 2026-09-23). A shopper who
 * opens the calendar and backs out has settled nothing, and the CTA keeps them
 * there. So nothing here consults a screen visit, and there is no `visited` map
 * to consult.
 *
 * Two places hold a pick and this module reads both:
 *
 * - `checkoutSlice.selections[ruleType]` — this session's pick, written by the
 *   picker screens through `useSetCheckoutRuleSelection` and **only** on the
 *   mutation's success. Lost on reload.
 * - `sessionData.checkoutRules` — the persisted pick the boot response carries.
 *   Survives reload. Since COM-1765 a landed write updates it
 *   (`setSessionRulePick`) and `selection_not_offered` recovery drops it
 *   (`dropSessionRulePick`), so it tracks the last confirmed pick rather than
 *   the page-load one; a rejected write leaves it alone.
 *
 * The fresh pick therefore wins and the persisted one backs it. That ordering
 * is what makes a reload safe *and* keeps the cart row from showing yesterday's
 * date for the rest of the session.
 */

/**
 * An option the shopper could actually be given.
 *
 * `data.length` is the wrong count. `[{ data: '', amount: 0 }]` has length one
 * and nothing writable — `select` early-returns on a falsy value, so a
 * length-based count would write nothing, answer `false`, and settle the rule
 * as `'failed'` over a rule that never failed. The same normalisation
 * `selectRulePickId` and `deriveUiFlags.confirmDisabled` already apply.
 *
 * Shared by `decideAutoSettle` and by `select`'s release of a `'failed'` rule
 * (`useCheckoutRuleSelection`, COM-1501), so both count options alike.
 */
export const isUsableOption = (option?: ICheckoutRuleValue): boolean =>
  typeof option?.data === 'string' && option.data !== ''

/**
 * The persisted pick for one rule type, or `undefined`.
 *
 * A reader of `session.checkoutRules`, beside `readSessionRulePickKeyedId`
 * (which keeps the `key`). `utils/cartCheckoutPanel.ts`
 * called a private `findData` for this until COM-1045; it now calls here, so
 * the panel and the settled-selector cannot disagree about what the array says.
 *
 * ⚠️ **An entry is not a pick.** `value` is a nullable
 * `SessionCheckoutRuleValue` and its `data` is `string | null | undefined`, so
 * a rule the storefront wrote with nothing chosen arrives as
 * `{ ruleType, value: null }` or `{ ruleType, value: { data: null } }`. Read
 * the chain as `?.value?.data`; `.value.data` throws on the first of those.
 *
 * `setCheckoutRuleById` filters and appends server-side, so the array holds at
 * most one entry per `ruleType`. `find` reflects that. A second entry for the
 * same type is ignored rather than rejected — cinnamon has no authority to
 * refuse a shape the server accepted, and picking between two would be a coin
 * toss.
 *
 * `ruleType` on the wire is `String!` with no enum behind it, so the array can
 * carry a type this build has never heard of. Comparing by value is safe
 * because `SessionRuleTypeMatchesConfig` in `getSession/types.ts` fails the
 * build if `SessionCheckoutRuleType` and `CheckoutRules` ever drift apart.
 */
export const readSessionRulePick = (
  rules: SessionCheckoutRule[] | null | undefined,
  ruleType: CheckoutRuleDocumentType,
): string | undefined => {
  if (!Array.isArray(rules) || rules.length === 0) return undefined

  const match = rules.find((rule) => rule?.ruleType === ruleType)
  const data = match?.value?.data

  return typeof data === 'string' && data !== '' ? data : undefined
}

/**
 * The shopper's pick for one rule type: this session's first, the persisted one
 * behind it.
 *
 * **The precedence is load-bearing in both directions.** Reading the session
 * copy first would show the last *confirmed* pick over an optimistic one still
 * in flight, since the snapshot moves only once a write lands (COM-1765).
 * Reading only the fresh copy would walk a returning shopper through pickers
 * they have already answered.
 *
 * Both sides are normalised to "a non-empty string, or nothing". The two types
 * disagree — `ICheckoutRuleValue.data` is a required `string` while the
 * session's is `string | null | undefined` — and without the normalisation an
 * empty string would read as settled on one side and unsettled on the other
 * for the same shopper.
 */
export const selectRulePick = (
  state: RootState,
  ruleType: CheckoutRuleDocumentType,
): string | undefined => {
  /*
   * Optional all the way down, matching `isHidePricesEnabled` beside it.
   * `checkoutSlice` is registered on the real store and cannot be absent at
   * runtime, but a `RootState` in this repo is routinely a partial object cast
   * into shape by a test fixture, and a selector that throws on one is a
   * selector nobody can call from a suite that does not care about checkout.
   */
  const fresh = state?.checkoutSlice?.selections?.[ruleType]?.data
  if (typeof fresh === 'string' && fresh !== '') return fresh

  return readSessionRulePick(
    state?.defaultSlice?.sessionData?.checkoutRules,
    ruleType,
  )
}

/**
 * The persisted pick's keyed id, or `undefined`.
 *
 * The sibling of `readSessionRulePick` that preserves the `key` when the server
 * copied one onto the pick (guide §2). Returns `key ?? data`, so an unkeyed
 * pick reads as `data` and the two readers agree on the unkeyed case.
 *
 * ⚠️ **Not a drop-in replacement for `readSessionRulePick`.** The cart panel
 * and the `settled` selector want the `data` — the payment-method enum value
 * or the calendar day — and `key ?? data` returns an opaque row id instead.
 * Use this one only where the id is being matched against an offered option's
 * `option.key ?? option.data`: the picker highlight and the auto-settle match.
 */
export const readSessionRulePickKeyedId = (
  rules: SessionCheckoutRule[] | null | undefined,
  ruleType: CheckoutRuleDocumentType,
): string | undefined => {
  if (!Array.isArray(rules) || rules.length === 0) return undefined

  const match = rules.find((rule) => rule?.ruleType === ruleType)
  const id = match?.value?.key ?? match?.value?.data

  return typeof id === 'string' && id !== '' ? id : undefined
}

/**
 * The shopper's pick for one rule type as a keyed row id: this session's first,
 * the persisted one behind it (COM-1253).
 *
 * The keyed sibling of `selectRulePick`. Mirrors the same precedence (slice
 * first, session fallback — "load-bearing in both directions" per the sibling's
 * docstring) and the same empty-string guard, but returns `key ?? data` on both
 * sides. Used by `getPaymentMethodSelectorProps` to highlight the right row on
 * reload, and by `useCheckoutRuleAutoSettle`'s persisted-pick branch to match
 * against offered options symmetrically with the fresh branch.
 *
 * On a keyed list, matching the persisted pick on `data` alone treats a saved
 * `CREDIT_60D` as offered whenever any `CREDIT` is offered. On an unkeyed list
 * `key` is absent on both sides and the id degenerates to `data`, so unkeyed
 * callers see no behaviour change against the sibling.
 */
export const selectRulePickId = (
  state: RootState,
  ruleType: CheckoutRuleDocumentType,
): string | undefined => {
  const sel = state?.checkoutSlice?.selections?.[ruleType]
  const fresh = sel?.key ?? sel?.data
  if (typeof fresh === 'string' && fresh !== '') return fresh

  return readSessionRulePickKeyedId(
    state?.defaultSlice?.sessionData?.checkoutRules,
    ruleType,
  )
}

const asLabel = (label: unknown): string | undefined =>
  typeof label === 'string' && label !== '' ? label : undefined

/**
 * The persisted pick's `attributes`, or `undefined`.
 *
 * Requires a non-empty `data` first: an entry is not a pick (see
 * `readSessionRulePick`), and attributes on an entry with nothing chosen would
 * describe a method the row does not show.
 */
const readSessionRulePickAttributes = (
  rules: SessionCheckoutRule[] | null | undefined,
  ruleType: CheckoutRuleDocumentType,
): ICheckoutRuleAttributes | undefined => {
  if (!readSessionRulePick(rules, ruleType)) return undefined

  const match = rules?.find((rule) => rule?.ruleType === ruleType)

  return match?.value?.attributes ?? undefined
}

/**
 * The shopper's picked option's `attributes` for one rule type, when it carried
 * any — the `label` and `termDays` the summary surfaces keep on screen past the
 * pick (COM-1259, COM-1710).
 *
 * **Match equality `(row.key ?? row.data) === (selection.key ?? selection.data)`.**
 * `useCheckoutRuleAutoSettle` picks a row with the same identity, so the
 * resolver stays in agreement with the hook that writes the pick. An unkeyed
 * list matches on `data`; a keyed list matches on `key`.
 *
 * **Two sources, chosen by which pick is live (COM-1712).** A pick made in this
 * page load reads its attributes off `checkoutSlice.available`, which the
 * picker's mount fetch populates. With no fresh pick — a reload, a link from
 * chat, a return to the webview — they come from the persisted pick's own
 * `attributes`, which the server copied from the offered option (guide §2).
 * The test is the one `selectRulePickId` uses, so the attributes always belong
 * to the pick the row shows. Falling back to the session on a fresh pick whose
 * row is missing from `available` would pair today's method with yesterday's
 * label and term: `sessionData` is patched only once a write lands
 * (`setSessionRulePick`, COM-1765), so while a fresh pick is in flight, or
 * after its write failed, the session still carries the previous pick.
 *
 * **The `row != null` guard matches the rest of the module's stance on this
 * list.** `available.*.data` is populated from unvalidated checkout-rule JSON
 * and stored as-is: `toPaymentMethodOptions`
 * (`utils/paymentMethodSelector.ts`) skips nullish entries, the picker's
 * selection writer walks the list with optional chaining, and
 * `useCheckoutRuleAutoSettle` filters unusable options. Dereferencing a
 * `null` row here would turn an ignored malformed option into a cart-render
 * throw on a shopper who already picked a valid one.
 *
 * The values inside are unchecked: each caller validates the member it reads,
 * as `selectRulePickLabel` and `formatTermDaysTag` do.
 */
export const selectRulePickAttributes = (
  state: RootState,
  ruleType: CheckoutRuleDocumentType,
): ICheckoutRuleAttributes | undefined => {
  const selection = state?.checkoutSlice?.selections?.[ruleType]
  const pickKey = selection?.key ?? selection?.data
  if (typeof pickKey !== 'string' || pickKey === '') {
    return readSessionRulePickAttributes(
      state?.defaultSlice?.sessionData?.checkoutRules,
      ruleType,
    )
  }

  const rows = state?.checkoutSlice?.available?.[ruleType]?.data
  if (!Array.isArray(rows)) return undefined

  const match = rows.find(
    (row) => row != null && (row.key ?? row.data) === pickKey,
  )

  return match?.attributes ?? undefined
}

/**
 * The shopper's picked label for one rule type, when the option carried one.
 *
 * `attributes.label` on a keyed `ICheckoutRuleValue` is the picked option's
 * display copy — "Crédito 14 días" against a `data: 'CREDIT'` row (guide §5).
 * Four summary surfaces consume this to keep that copy on screen past the
 * pick: the cart's `Pago` row via `CartCheckoutPanel`'s `paymentMethodLabel`
 * prop, the dialog's `Pago` row via `confirmOrderDialog`'s local resolver,
 * and `OrderConfirmationPanel`/`OrderDetailPanel` through the sibling
 * injectors (COM-1259).
 *
 * Read through `selectRulePickAttributes`, so the label and the term the cart
 * shows beside it (COM-1710) always come from the same pick by the same rule.
 *
 * **No map fallback.** Each caller owns its `paymentMethodLabels` and the
 * `??` suppression contract that goes with it. Returning `undefined` lets the
 * existing resolver render; returning a map value here would duplicate that
 * reasoning and would collide with the deliberate `labels[method] === ''`
 * suppression on the dialog.
 */
export const selectRulePickLabel = (
  state: RootState,
  ruleType: CheckoutRuleDocumentType,
): string | undefined =>
  asLabel(selectRulePickAttributes(state, ruleType)?.label)

/**
 * Whether the shopper has chosen a value for this rule.
 *
 * The one thing COM-1046's guided flow and COM-1048's auto-settle ask. Curried
 * so it reads as `useAppSelector(selectRuleIsSettled(ruleType))`, matching
 * `selectSelectionByRuleType` and `selectAvailableByRuleType` in
 * `redux/slices/checkout/checkout.ts`.
 *
 * It lives in `utils/` rather than beside those two because it spans slices —
 * the same reason `selectOrderDistributor` and `isHidePricesEnabled` do.
 *
 * ⚠️ **Settled says nothing about whether the rule was changeable.** A rule
 * with one option is settled by COM-1048 writing it, and a rule with no
 * `checkoutRuleRoutes` entry is settled by whatever the storefront persisted.
 * Ask `selectFirstHeldRule` below for changeability; asking this instead
 * strands a shopper on a CTA that goes nowhere.
 *
 * (This paragraph named a `resolveRuleChangeRoute` that never existed in any
 * build. COM-1046 wrote the function it was reaching for.)
 */
export const selectRuleIsSettled =
  (ruleType: CheckoutRuleDocumentType) =>
  (state: RootState): boolean =>
    selectRulePick(state, ruleType) !== undefined

/**
 * Auto-settle outcomes that leave the shopper a choice still to make.
 *
 * The set `selectAutoSettleStatus` prescribes for this ticket, and its
 * docstring carries the reasoning rather than repeating it here.
 *
 * ⚠️ **`undefined` is absent, and that omission is load-bearing.** It means
 * nothing decided — a rule the storefront does not serve, or a screen that
 * never mounted `useCheckoutRuleAutoSettle`. Reading it as "the shopper must
 * pick" holds the CTA at *Continuar* on a read-only storefront. The route test
 * below would catch that one anyway, so including it would be *safe* rather
 * than right; the contract says `undefined` is not `'multiple'`, and a quiet
 * deviation from it is the thing that bites the next reader.
 *
 * ⚠️ **`'empty'` and `'unavailable'` are absent too, and both are unsettled.**
 * A predicate written as "unsettled and routable" reads as equivalent and
 * bricks checkout: a rule offering nothing can never be chosen, so it would
 * hold the CTA for ever. That is the case COM-1048 exists to prevent, and it
 * is reachable on the pilot — `validateDates` drops the computed day unless it
 * falls strictly after today.
 */
const HOLDS_CTA: AutoSettleStatus[] = ['multiple', 'failed']

/** A rule still holding the CTA, and the screen that settles it. */
export interface HeldCheckoutRule {
  ruleType: CheckoutRuleDocumentType
  path: string
}

/**
 * The first rule still holding the CTA at *Continuar*, and where to send the
 * shopper to settle it — or `undefined` when they are done choosing.
 *
 * **One function answers two questions, which is the point.** The summary
 * CTA's label is `selectFirstHeldRule(state) !== undefined` and its tap target
 * is the same call's `path`. A separate predicate for the label would let a
 * button read *Continuar* and route nowhere.
 *
 * **Order comes from `CHECKOUT_RULE_DOCUMENT_TYPES`**, which is
 * `[DELIVERY_DATES, PAYMENT_METHODS]`. That array is what produces the
 * summary -> delivery -> payment -> summary walk; reordering it reorders the
 * walk. Same "first routable wins" posture as `useCheckoutRuleRedirect`.
 *
 * **The four tests run cheapest first** — the auto-settle map read, the
 * `checkoutRuleSettings.required` read (COM-1255), the settled lookup, then
 * `matchRoutes`. A storefront authoring no `checkoutRuleRoutes` never reaches
 * the fourth, because `useCheckoutRuleAutoSettle` leaves every status
 * `undefined` there. This runs on every store action while the summary is
 * mounted.
 *
 * ⚠️ **Reachability is not the auto-settle's `served`.** That flag tests key
 * presence on `checkoutRuleRoutes`; this tests `matchRoutes` against the
 * authored table. A storefront that authors the map entry and forgets the
 * route makes the first true and the second false, and gating on the first
 * would leave the CTA reading *Continuar* with nowhere to go, permanently.
 *
 * ⚠️ **`'failed'` skips the settled test on purpose.** A returning shopper
 * carries yesterday's pick on `session.checkoutRules`. The auto-settle fetches
 * today's single option, writes, is rejected, and rolls back to *nothing* in
 * the slice — so `selectRulePick` falls through to the session copy and the
 * rule reads settled on a value the storefront no longer offers. `'failed'` is
 * a positive claim that the stored value is wrong, so it outranks that.
 * **It is not permanent.** A pick that lands on the server releases the rule
 * (`releaseFailedAutoSettle`, called from `useCheckoutRuleSelection.select`,
 * COM-1501). Until then `'failed'` held the summary at *Continuar* for the rest
 * of the session — an earlier revision of this paragraph called that "one
 * picker visit", and nothing ever moved the status, so it was every visit and
 * no order. The mirror case — the deadline fires during `'writing'` and the
 * mutation then lands — now releases on that late answer too, since the value
 * it wrote is on the session; `advance('settled')` is still refused as late,
 * and the release comes from `select` instead. A rejected pick leaves the rule
 * `'failed'`. Walking someone to a screen is recoverable; shipping a delivery
 * date the storefront withdrew is not.
 *
 * `skip` excludes one rule type, and the picker screens' `confirm()` passes
 * their own. `confirm()` is ungated on a selection existing by design, so on a
 * storefront that never bound `checkout.ui.<RULE>.confirmDisabled` a shopper
 * can confirm having chosen nothing — and without `skip` they would be pushed
 * at the screen they are standing on, a button that visibly does nothing.
 *
 * The origin guard lives here rather than at each call site, so every caller
 * is safe by construction. See `ESCAPES_ORIGIN`.
 */
export const selectFirstHeldRule = (
  state: RootState,
  skip?: CheckoutRuleDocumentType,
): HeldCheckoutRule | undefined =>
  CHECKOUT_RULE_DOCUMENT_TYPES.reduce<HeldCheckoutRule | undefined>(
    (found, ruleType) => {
      if (found || ruleType === skip) return found

      /*
       * Optional all the way down, matching `selectRulePick` above and for its
       * reason: a `RootState` here is routinely a partial test fixture.
       */
      const status = state?.checkoutSlice?.autoSettle?.[ruleType]
      if (!status || !HOLDS_CTA.includes(status)) return undefined

      /*
       * COM-1255: a storefront that opts in with `required: false` releases
       * the walk for this rule, chosen or not. **A missing setting defaults to
       * required**, because today's queue forces a pick on every multi-option
       * rule and no live storefront ships `checkoutRuleSettings` (COM-1184 PR
       * 4 selected the shape; this is its first production reader). The
       * explicit `=== false` collapses the four "defaults" shapes
       * `selectCheckoutRuleSettingByType` documents — the slice-level
       * `undefined`, the field-level `null` (GraphQL omits a selected-but-
       * unauthored field), the field-level `undefined`, and the literal
       * `true` — all into "required".
       *
       * This outranks the `'failed'` branch below: a rule the storefront
       * does not require should not strand the shopper on *Continuar* over a
       * rolled-back auto-settle write. For a required rule the branch stands.
       */
      const setting = selectCheckoutRuleSettingByType(ruleType)(state)
      if (setting?.required === false) return undefined

      if (status !== 'failed' && selectRuleIsSettled(ruleType)(state)) {
        return undefined
      }

      const path = resolveCheckoutRuleRoute(
        state?.defaultSlice?.config?.checkoutRuleRoutes?.[ruleType],
        state?.defaultSlice?.config?.routes,
        state?.defaultSlice?.storeName,
        state?.defaultSlice?.sessionId,
      )
      if (!path || ESCAPES_ORIGIN.test(path)) return undefined

      return { ruleType, path }
    },
    undefined,
  )

export default selectRuleIsSettled
